"use client";

import Cta from "@/components/Cta";
import PropertyCard from "@/components/PropertyCard";
import Vague from "@/components/Vague";
import { dedupeDocumentsById, docToCardItem } from "@/script/propertyDocument";
import { isFilled } from "@prismicio/client";
import { PrismicImage, PrismicLink, PrismicRichText } from "@prismicio/react";
import { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const FILTERS = [
  { id: "propertyType", label: "Type de bien" },
  { id: "location", label: "Localisation" },
  { id: "surface", label: "Surface" },
  { id: "rooms", label: "Pièces" },
  { id: "budget", label: "Budget" },
];

const PROPERTY_TYPES = ["Appartement", "Maison", "Parking", "Local Commercial"];

const ROOM_OPTIONS = [
  { value: "1", label: "1 pièce" },
  { value: "2", label: "2 pièces" },
  { value: "3", label: "3 pièces" },
  { value: "4", label: "4 pièces" },
  { value: "5 et +", label: "5 pièces et +" },
];

/** Paliers : +10 jusqu'à 150, +25 jusqu'à 300, +50 jusqu'à 450. */
function buildSurfaceSliderValues() {
  const values = [];
  for (let v = 0; v <= 150; v += 10) values.push(v);
  for (let v = 175; v <= 300; v += 25) values.push(v);
  for (let v = 350; v <= 450; v += 50) values.push(v);
  return values;
}

const SURFACE_SLIDER_VALUES = buildSurfaceSliderValues();
const SURFACE_MAX_INDEX = SURFACE_SLIDER_VALUES.length - 1;
const SURFACE_MIN_GAP = 1;
/** @typedef {typeof EMPTY_FILTERS} PropertyFilters */

const EMPTY_FILTERS = {
  propertyTypes: [],
  cities: [],
  surfaceMin: null,
  surfaceMax: null,
  rooms: [],
  budgetMin: null,
  budgetMax: null,
};

const DEBOUNCE_MS = 300;
const PAGE_SIZE = 9;

const FILTER_ACTIVE_CHECKS = {
  propertyType: (f) => f.propertyTypes.length > 0,
  location: (f) => f.cities.length > 0,
  surface: (f) => f.surfaceMin != null || f.surfaceMax != null,
  rooms: (f) => f.rooms.length > 0,
  budget: (f) => f.budgetMin != null || f.budgetMax != null,
};

const FILTER_SELECTION_COUNTS = {
  propertyType: (f) => f.propertyTypes.length,
  location: (f) => f.cities.length,
  surface: (f) => Number(f.surfaceMin != null || f.surfaceMax != null),
  rooms: (f) => f.rooms.length,
  budget: (f) => Number(f.budgetMin != null || f.budgetMax != null),
};

/** @param {string | number | null | undefined} value */
function parseNumeric(value) {
  if (value == null || value === "") return null;
  const n = Number(String(value).replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {typeof EMPTY_FILTERS} filters
 * @param {string} key
 */
function isFilterGroupActive(filters, key) {
  return FILTER_ACTIVE_CHECKS[key]?.(filters) ?? false;
}

/**
 * @param {typeof EMPTY_FILTERS} filters
 * @param {string} key
 */
function getFilterGroupSelectionCount(filters, key) {
  return FILTER_SELECTION_COUNTS[key]?.(filters) ?? 0;
}

/**
 * @param {typeof EMPTY_FILTERS} filters
 * @param {{ id: string }[]} filterGroups
 */
function getTotalSelectedFilterCount(filters, filterGroups) {
  return filterGroups.reduce(
    (total, filterGroup) => total + getFilterGroupSelectionCount(filters, filterGroup.id),
    0
  );
}

/** @param {string} tag */
function normalizeTag(tag) {
  return tag
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

/** @param {import("@prismicio/client").PrismicDocument} doc */
function isCoupDeCoeurProperty(doc) {
  const tags = doc.tags;
  if (!Array.isArray(tags)) return false;
  return tags.some((tag) =>
    typeof tag === "string" && normalizeTag(tag) === "coup de cœur"
  );
}

/** @param {string | number | null | undefined} value @param {"property" | "property_rental"} documentType */
function normalizeRoomNumbers(value, documentType) {
  if (value == null || value === "") return null;

  if (documentType === "property_rental") {
    const count = Number(value);
    if (!Number.isFinite(count)) return null;
    if (count >= 5) return "5 et +";
    return String(count);
  }

  return String(value);
}

/** @param {Array<Record<string, unknown>>} properties */
function prioritizeCoupDeCoeur(properties) {
  return properties
    .map((property, index) => ({ property, index }))
    .sort((a, b) => {
      const aPriority = isCoupDeCoeurProperty(a.property) ? 1 : 0;
      const bPriority = isCoupDeCoeurProperty(b.property) ? 1 : 0;
      if (aPriority !== bPriority) {
        return bPriority - aPriority;
      }
      return a.index - b.index;
    })
    .map(({ property }) => property);
}

const FILTER_BUTTON_CLASSNAME =
  "cursor-pointer relative flex py-2 w-full items-center justify-between rounded-lg bg-bleu-900 px-4 text-bleu-50 transition-colors hover:bg-bleu-800";

function PlusIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M17 11L13 11V7C13 6.73478 12.8946 6.48043 12.7071 6.29289C12.5196 6.10536 12.2652 6 12 6C11.7348 6 11.4804 6.10536 11.2929 6.29289C11.1054 6.48043 11 6.73478 11 7V11H7C6.73478 11 6.48043 11.1054 6.29289 11.2929C6.10536 11.4804 6 11.7348 6 12C6 12.2652 6.10536 12.5196 6.29289 12.7071C6.48043 12.8946 6.73478 13 7 13H11L11 17C11 17.2652 11.1054 17.5196 11.2929 17.7071C11.4804 17.8946 11.7348 18 12 18C12.2652 18 12.5196 17.8946 12.7071 17.7071C12.8946 17.5196 13 17.2652 13 17L13 13L17 13C17.2652 13 17.5196 12.8946 17.7071 12.7071C17.8946 12.5196 18 12.2652 18 12C18 11.7348 17.8946 11.4804 17.7071 11.2929C17.5196 11.1054 17.2652 11 17 11Z"
        fill="#EFF7FB"
      />
    </svg>
  );
}

/**
 * @param {import("@prismicio/client").PrismicDocument[]} documents
 * @param {typeof EMPTY_FILTERS} filters
 * @param {"property" | "property_rental"} documentType
 */
function filterProperties(documents, filters, documentType) {
  const { surfaceMin, surfaceMax, budgetMin, budgetMax, rooms } = filters;

  return documents.filter((doc) => {
    const data = doc.data ?? {};
    const normalizedState =
      typeof data.state === "string"
        ? data.state
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .trim()
        : "";

    if (documentType === "property_rental") {
      if (normalizedState !== "a louer") {
        return false;
      }
    } else if (normalizedState === "vendu") {
      return false;
    }

    if (
      filters.propertyTypes.length > 0 &&
      !filters.propertyTypes.includes(data.property_type)
    ) {
      return false;
    }

    const city = data.city ?? data.district ?? null;
    if (filters.cities.length > 0 && (!city || !filters.cities.includes(city))) {
      return false;
    }

    if (surfaceMin != null || surfaceMax != null) {
      const surface = parseNumeric(data.surface);
      if (surface == null) return false;
      if (surfaceMin != null && surface < surfaceMin) return false;
      if (surfaceMax != null && surface > surfaceMax) return false;
    }

    if (rooms.length > 0) {
      const roomNumbers = normalizeRoomNumbers(data.room_numbers, documentType);
      if (!roomNumbers || !rooms.includes(roomNumbers)) {
        return false;
      }
    }

    if (budgetMin != null || budgetMax != null) {
      const price = parseNumeric(data.price);
      if (price == null) return false;
      if (budgetMin != null && price < budgetMin) return false;
      if (budgetMax != null && price > budgetMax) return false;
    }

    return true;
  });
}

/**
 * @param {string[]} cities
 */
function getAvailableCities(cities) {
  return [...new Set(cities.filter((city) => city && city.trim() !== ""))].sort(
    (a, b) => a.localeCompare(b, "fr")
  );
}

/**
 * @param {{
 *   label: string;
 *   isOpen: boolean;
 *   indicatorCount?: number;
 *   onToggle: () => void;
 *   onClose: () => void;
 *   children: import("react").ReactNode;
 *   panelClassName?: string;
 * }} props
 */
function FilterButton({
  label,
  isOpen,
  indicatorCount = 0,
  onToggle,
  onClose,
  children,
  panelClassName = "",
}) {
  const rootRef = useRef(null);
  const panelId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        onCloseRef.current();
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") onCloseRef.current();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  return (
    <div ref={rootRef} className="relative w-[190px]">
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
        className={FILTER_BUTTON_CLASSNAME}
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate body-s">{label}</span>
          {indicatorCount > 0 ? (
            <span className="inline-flex py-0.5 items-center justify-center rounded-full bg-terracota-400 px-1.5 text-[12px]/[18px] font-semibold text-terracota-100">
              {indicatorCount}
            </span>
          ) : null}
        </span>
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="currentColor"
          className={`shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
          aria-hidden
        >
          <path d="M7.41 8.59L12 13.17L16.59 8.59L18 10L12 16L6 10L7.41 8.59Z" />
        </svg>
      </button>

      {isOpen ? (
        <div
          id={panelId}
          role="dialog"
          className={`filter-dropdown-panel absolute left-0 top-[calc(100%+10px)] z-120 ${panelClassName}`}
        >
          <div className="filter-dropdown-panel-inner">{children}</div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * @param {{
 *   label: string;
 *   isOpen: boolean;
 *   indicatorCount?: number;
 *   onToggle: () => void;
 *   children: import("react").ReactNode;
 * }} props
 */
function MobileFilterSection({ label, isOpen, indicatorCount = 0, onToggle, children }) {
  return (
    <div className="w-full">
      <button
        type="button"
        onClick={onToggle}
        className={FILTER_BUTTON_CLASSNAME}
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate body-s">{label}</span>
          {indicatorCount > 0 ? (
            <span className="inline-flex min-w-5  py-0.5 items-center justify-center rounded-full bg-terracota-400 px-1.5 text-[12px]/[18px] font-semibold text-terracota-100">
              {indicatorCount}
            </span>
          ) : null}
        </span>
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="currentColor"
          className={`shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
          aria-hidden
        >
          <path d="M7.41 8.59L12 13.17L16.59 8.59L18 10L12 16L6 10L7.41 8.59Z" />
        </svg>
      </button>

      {isOpen ? <div className="filter-dropdown-panel-inner w-full my-4">{children}</div> : null}
    </div>
  );
}

/**
 * @param {{
 *   items: { value: string; label: string }[];
 *   selected: string[];
 *   onToggle: (value: string) => void;
 *   scrollable?: boolean;
 *   highlightSelected?: boolean;
 * }} props
 */
function CheckboxFilterList({
  items,
  selected,
  onToggle,
  scrollable = false,
  highlightSelected = false,
}) {
  const listClass = scrollable
    ? "max-h-[240px] overflow-y-auto py-2 text-[18px]/[26px] text-bleu-950"
    : "py-2 text-[18px]/[26px] text-bleu-950";

  return (
    <ul className={listClass}>
      {items.map(({ value, label }) => {
        const checked = selected.includes(value);
        const rowClass = highlightSelected
          ? `flex min-h-12 cursor-pointer gap-4 px-6 items-center transition-colors hover:bg-bleu-100 ${checked ? "bg-bleu-100" : "bg-blanc"
          }`
          : "flex min-h-12 cursor-pointer gap-4 px-6 items-center transition-colors hover:bg-bleu-50";

        return (
          <li key={value}>
            <div>
              <label className={rowClass}>
                <input
                  type="checkbox"
                  className="h-5 min-w-5 rounded-[6px] mt-[2px]"
                  checked={checked}
                  onChange={() => onToggle(value)}
                />
                {label}
              </label>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * @param {number | null} min
 * @param {number | null} max
 */
function formatSurfaceRangeLabel(min, max) {
  const minLabel = min != null ? `${min} m²` : "0 m²";
  const maxLabel =
    max != null && max >= SURFACE_SLIDER_VALUES[SURFACE_MAX_INDEX]
      ? "450+ m²"
      : max != null
        ? `${max} m²`
        : "450+ m²";
  return `${minLabel} – ${maxLabel}`;
}

/**
 * @param {number} value
 */
function getSurfaceSliderIndex(value) {
  const index = SURFACE_SLIDER_VALUES.indexOf(value);
  return index >= 0 ? index : 0;
}

/**
 * @param {{
 *   surfaceMin: number | null;
 *   surfaceMax: number | null;
 *   onChange: (patch: Partial<PropertyFilters>) => void;
 * }} props
 */
function SurfaceRangeSlider({ surfaceMin, surfaceMax, onChange }) {
  const isActive = surfaceMin != null || surfaceMax != null;
  const currentMinIndex =
    surfaceMin != null ? getSurfaceSliderIndex(surfaceMin) : 0;
  const currentMaxIndex =
    surfaceMax != null
      ? getSurfaceSliderIndex(surfaceMax)
      : SURFACE_MAX_INDEX;

  const commitRange = (minIndex, maxIndex) => {
    const atFullRange = minIndex <= 0 && maxIndex >= SURFACE_MAX_INDEX;
    if (atFullRange) {
      onChange({ surfaceMin: null, surfaceMax: null });
      return;
    }

    const minValue = SURFACE_SLIDER_VALUES[minIndex];
    const maxValue = SURFACE_SLIDER_VALUES[maxIndex];
    onChange({
      surfaceMin: minIndex > 0 ? minValue : null,
      surfaceMax: maxValue,
    });
  };

  const updateMin = (index) => {
    const nextMinIndex = Math.min(
      Number(index),
      Math.max(0, currentMaxIndex - SURFACE_MIN_GAP)
    );
    commitRange(nextMinIndex, currentMaxIndex);
  };

  const updateMax = (index) => {
    const nextMaxIndex = Math.max(
      Number(index),
      Math.min(SURFACE_MAX_INDEX, currentMinIndex + SURFACE_MIN_GAP)
    );
    commitRange(currentMinIndex, nextMaxIndex);
  };

  const rangeSpan = SURFACE_MAX_INDEX || 1;

  return (
    <div className="flex w-full flex-col gap-3 px-4 py-4">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[18px]/[26px] font-medium text-bleu-950">Surface</span>
        <span className="text-right text-[15px] text-bleu-800">
          {isActive
            ? formatSurfaceRangeLabel(surfaceMin, surfaceMax)
            : "Toutes (0 – 450+ m²)"}
        </span>
      </div>

      <div className="relative flex h-8 items-center">
        <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-bleu-900/15" />
        <div
          className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-bleu-900"
          style={{
            left: `${(currentMinIndex / rangeSpan) * 100}%`,
            right: `${100 - (currentMaxIndex / rangeSpan) * 100}%`,
          }}
        />
        <input
          type="range"
          min={0}
          max={SURFACE_MAX_INDEX}
          step={1}
          value={currentMinIndex}
          onChange={(event) => updateMin(event.target.value)}
          className="surface-range-input pointer-events-none absolute inset-x-0 top-1/2 h-[18px] w-full -translate-y-1/2 appearance-none bg-transparent [&::-webkit-slider-thumb]:pointer-events-auto"
          aria-label="Surface minimum"
        />
        <input
          type="range"
          min={0}
          max={SURFACE_MAX_INDEX}
          step={1}
          value={currentMaxIndex}
          onChange={(event) => updateMax(event.target.value)}
          className="surface-range-input pointer-events-none absolute inset-x-0 top-1/2 z-10 h-[18px] w-full -translate-y-1/2 appearance-none bg-transparent [&::-moz-range-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:pointer-events-auto"
          aria-label="Surface maximum"
        />
      </div>

      <div className="flex justify-between text-[15px] text-bleu-800/80">
        <span>0 m²</span>
        <span>450+ m²</span>
      </div>
    </div>
  );
}

const BUDGET_FIELDS = [
  { key: "budgetMin", label: "Minimum" },
  { key: "budgetMax", label: "Maximum" },
];

/**
 * @param {{
 *   budgetMin: number | null;
 *   budgetMax: number | null;
 *   onChange: (patch: Partial<PropertyFilters>) => void;
 * }} props
 */
function BudgetRangeInputs({ budgetMin, budgetMax, onChange }) {
  const values = { budgetMin, budgetMax };

  return (
    <div className="flex flex-col gap-4 p-4 pt-6">
      {BUDGET_FIELDS.map(({ key, label }) => (
        <label key={key} className="flex flex-col gap-2">
          <span className="text-[15px] font-medium text-bleu-800">{label}</span>
          <input
            type="number"
            min={0}
            placeholder="0"
            value={values[key] ?? ""}
            onChange={(e) =>
              onChange({
                [key]: e.target.value === "" ? null : Number(e.target.value),
              })
            }
            className="h-[50px] rounded bg-bleu-50 px-4 text-bleu-950"
          />
        </label>
      ))}
    </div>
  );
}

/**
 * @param {{
 *   filterId: string;
 *   draft: typeof EMPTY_FILTERS;
 *   cities: string[];
 *   onDraftChange: (patch: Partial<typeof EMPTY_FILTERS>) => void;
 *   onToggleListItem: (key: "propertyTypes" | "cities" | "rooms", value: string) => void;
 * }} props
 */
function FilterPopupContent({
  filterId,
  draft,
  cities,
  onDraftChange,
  onToggleListItem,
}) {
  switch (filterId) {
    case "propertyType":
      return (
        <CheckboxFilterList
          items={PROPERTY_TYPES.map((type) => ({ value: type, label: type }))}
          selected={draft.propertyTypes}
          onToggle={(value) => onToggleListItem("propertyTypes", value)}
          highlightSelected
        />
      );
    case "location":
      if (cities.length === 0) {
        return (
          <p className="px-6 py-4 text-[18px]/[26px] text-bleu-950">
            Aucune ville disponible.
          </p>
        );
      }
      return (
        <CheckboxFilterList
          items={cities.map((city) => ({ value: city, label: city }))}
          selected={draft.cities}
          onToggle={(value) => onToggleListItem("cities", value)}
          scrollable
        />
      );
    case "surface":
      return (
        <SurfaceRangeSlider
          surfaceMin={draft.surfaceMin}
          surfaceMax={draft.surfaceMax}
          onChange={onDraftChange}
        />
      );
    case "rooms":
      return (
        <CheckboxFilterList
          items={ROOM_OPTIONS}
          selected={draft.rooms}
          onToggle={(value) => onToggleListItem("rooms", value)}
        />
      );
    case "budget":
      return (
        <BudgetRangeInputs
          budgetMin={draft.budgetMin}
          budgetMax={draft.budgetMax}
          onChange={onDraftChange}
        />
      );
    default:
      return null;
  }
}

/**
 * @param {{ info: Record<string, unknown> | null }} props
 */
function EmptyResultsContact({ info }) {
  if (!info) return null;

  return (
    <div className="w-full text-bleu-950">
      <div className="padding-y mx-auto flex max-w-[950px] flex-col items-center justify-center gap-10 padding-x md:flex-row-reverse lg:gap-18">
        <div className="flex flex-1 flex-col gap-5">
          {isFilled.richText(info.overtitle) && (
            <PrismicRichText
              field={info.overtitle}
              components={{
                heading3: ({ children }) => <h3 className="body-m text-bleu-950">{children}</h3>,
                heading4: ({ children }) => <h4 className="body-s text-bleu-950">{children}</h4>,
              }}
            />
          )}
          {isFilled.richText(info.title) && (
            <PrismicRichText
              field={info.title}
              components={{
                heading2: ({ children }) => <h2 className="h2 text-terracota-400">{children}</h2>,
                heading3: ({ children }) => <h3 className="h2 text-terracota-400">{children}</h3>,
              }}
            />
          )}
          {isFilled.richText(info.subtitle) && (
            <PrismicRichText
              field={info.subtitle}
              components={{
                heading3: ({ children }) => <h3 className="body-m text-bleu-950">{children}</h3>,
                heading4: ({ children }) => <h4 className="body-s text-bleu-950">{children}</h4>,
              }}
            />
          )}

          {isFilled.richText(info.text) && (
            <PrismicRichText
              field={info.text}
              components={{
                paragraph: ({ children }) => <p className="body-s">{children}</p>,
                strong: ({ children }) => <strong>{children}</strong>,
                em: ({ children }) => <em>{children}</em>,
                hyperlink: ({ children, node }) => (
                  <PrismicLink field={node.data}>{children}</PrismicLink>
                ),
                list: ({ children }) => <ul>{children}</ul>,
                listItem: ({ children }) => <li>{children}</li>,
                oList: ({ children }) => <ol>{children}</ol>,
                oListItem: ({ children }) => <li>{children}</li>,
              }}
            />
          )}

          {isFilled.keyText(info.phone_number) && (
            <a href={`tel:${info.phone_number}`} className="flex items-center gap-2.5">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.4 20.6L14.15 22.9C13.95 23.1 13.7167 23.2 13.45 23.2C13.1833 23.2 12.95 23.1 12.75 22.9C11.1667 21.4333 9.97917 19.7416 9.1875 17.825C8.39583 15.9083 8 13.9666 8 12C8 10.0333 8.39583 8.09579 9.1875 6.18745C9.97917 4.27912 11.1667 2.58329 12.75 1.09995C12.95 0.899951 13.1833 0.799952 13.45 0.799952C13.7167 0.799952 13.95 0.899951 14.15 1.09995L16.4 3.39995C16.5833 3.58328 16.6833 3.79578 16.7 4.03745C16.7167 4.27912 16.65 4.49995 16.5 4.69995L14.3 7.59995C14.2 7.73328 14.0833 7.83329 13.95 7.89995C13.8167 7.96662 13.6667 7.99995 13.5 7.99995L10.65 7.99995C10.45 8.63329 10.2917 9.28329 10.175 9.94995C10.0583 10.6166 10 11.3 10 12C10 12.7 10.0583 13.3833 10.175 14.05C10.2917 14.7166 10.45 15.3666 10.65 16L13.5 16C13.6667 16 13.8167 16.0333 13.95 16.1C14.0833 16.1666 14.2 16.2666 14.3 16.4L16.5 19.3C16.65 19.5 16.7167 19.7208 16.7 19.9625C16.6833 20.2041 16.5833 20.4166 16.4 20.6Z" fill="#0C2631" />
              </svg>
              <p>{info.phone_number}</p>
            </a>
          )}

          {isFilled.keyText(info.mail) && (
            <a href={`mailto:${info.mail}`} className="flex items-center gap-2.5">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 20C3.45 20 2.97917 19.8042 2.5875 19.4125C2.19583 19.0208 2 18.55 2 18V6C2 5.45 2.19583 4.97917 2.5875 4.5875C2.97917 4.19583 3.45 4 4 4H20C20.55 4 21.0208 4.19583 21.4125 4.5875C21.8042 4.97917 22 5.45 22 6V18C22 18.55 21.8042 19.0208 21.4125 19.4125C21.0208 19.8042 20.55 20 20 20H4ZM12 13L4 8V18H20V8L12 13ZM12 11L20 6H4L12 11Z" fill="#0C2631" />
              </svg>
              <p>{info.mail}</p>
            </a>
          )}

          <div className="pt-2">
            {isFilled.link(info.cta) && (
              <Cta color="bleu-950" justify="justify-center md:justify-start" field={info.cta} />
            )}
          </div>
        </div>

        <div className="flex flex-col items-center gap-6">
          {isFilled.image(info.picture) && (
            <div className="relative">
              <PrismicImage
                className="h-70 w-70 object-contain"
                field={{
                  ...info.picture,
                  alt: info.picture.alt || "Image d'illustration",
                }}
              />
              <div className="absolute -right-2 bottom-0.5">
                <Vague />
              </div>
            </div>
          )}
          <div className="flex flex-col items-center gap-1">
            {isFilled.keyText(info.name) && <h3 className="body-m">{info.name}</h3>}
            {isFilled.keyText(info.post) && <h4 className="body-s">{info.post}</h4>}
            <div className="flex items-center gap-2.5">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C10.6333 22 9.34167 21.7375 8.125 21.2125C6.90833 20.6875 5.84583 19.9708 4.9375 19.0625C4.02917 18.1542 3.3125 17.0917 2.7875 15.875C2.2625 14.6583 2 13.3667 2 12C2 10.6167 2.2625 9.32083 2.7875 8.1125C3.3125 6.90417 4.02917 5.84583 4.9375 4.9375C5.84583 4.02917 6.90833 3.3125 8.125 2.7875C9.34167 2.2625 10.6333 2 12 2C13.3833 2 14.6792 2.2625 15.8875 2.7875C17.0958 3.3125 18.1542 4.02917 19.0625 4.9375C19.9708 5.84583 20.6875 6.90417 21.2125 8.1125C21.7375 9.32083 22 10.6167 22 12C22 13.3667 21.7375 14.6583 21.2125 15.875C20.6875 17.0917 19.9708 18.1542 19.0625 19.0625C18.1542 19.9708 17.0958 20.6875 15.8875 21.2125C14.6792 21.7375 13.3833 22 12 22ZM12 19.95C12.4333 19.35 12.8083 18.725 13.125 18.075C13.4417 17.425 13.7 16.7333 13.9 16H10.1C10.3 16.7333 10.5583 17.425 10.875 18.075C11.1917 18.725 11.5667 19.35 12 19.95ZM9.4 19.55C9.1 19 8.8375 18.4292 8.6125 17.8375C8.3875 17.2458 8.2 16.6333 8.05 16H5.1C5.58333 16.8333 6.1875 17.5583 6.9125 18.175C7.6375 18.7917 8.46667 19.25 9.4 19.55ZM14.6 19.55C15.5333 19.25 16.3625 18.7917 17.0875 18.175C17.8125 17.5583 18.4167 16.8333 18.9 16H15.95C15.8 16.6333 15.6125 17.2458 15.3875 17.8375C15.1625 18.4292 14.9 19 14.6 19.55ZM4.25 14H7.65C7.6 13.6667 7.5625 13.3375 7.5375 13.0125C7.5125 12.6875 7.5 12.35 7.5 12C7.5 11.65 7.5125 11.3125 7.5375 10.9875C7.5625 10.6625 7.6 10.3333 7.65 10H4.25C4.16667 10.3333 4.10417 10.6625 4.0625 10.9875C4.02083 11.3125 4 11.65 4 12C4 12.35 4.02083 12.6875 4.0625 13.0125C4.10417 13.3375 4.16667 13.6667 4.25 14ZM9.65 14H14.35C14.4 13.6667 14.4375 13.3375 14.4625 13.0125C14.4875 12.6875 14.5 12.35 14.5 12C14.5 11.65 14.4875 11.3125 14.4625 10.9875C14.4375 10.6625 14.4 10.3333 14.35 10H9.65C9.6 10.3333 9.5625 10.6625 9.5375 10.9875C9.5125 11.3125 9.5 11.65 9.5 12C9.5 12.35 9.5125 12.6875 9.5375 13.0125C9.5625 13.3375 9.6 13.6667 9.65 14ZM16.35 14H19.75C19.8333 13.6667 19.8958 13.3375 19.9375 13.0125C19.9792 12.6875 20 12.35 20 12C20 11.65 19.9792 11.3125 19.9375 10.9875C19.8958 10.6625 19.8333 10.3333 19.75 10H16.35C16.4 10.3333 16.4375 10.6625 16.4625 10.9875C16.4875 11.3125 16.5 11.65 16.5 12C16.5 12.35 16.4875 12.6875 16.4625 13.0125C16.4375 13.3375 16.4 13.6667 16.35 14ZM15.95 8H18.9C18.4167 7.16667 17.8125 6.44167 17.0875 5.825C16.3625 5.20833 15.5333 4.75 14.6 4.45C14.9 5 15.1625 5.57083 15.3875 6.1625C15.6125 6.75417 15.8 7.36667 15.95 8ZM10.1 8H13.9C13.7 7.26667 13.4417 6.575 13.125 5.925C12.8083 5.275 12.4333 4.65 12 4.05C11.5667 4.65 11.1917 5.275 10.875 5.925C10.5583 6.575 10.3 7.26667 10.1 8ZM5.1 8H8.05C8.2 7.36667 8.3875 6.75417 8.6125 6.1625C8.8375 5.57083 9.1 5 9.4 4.45C8.46667 4.75 7.6375 5.20833 6.9125 5.825C6.1875 6.44167 5.58333 7.16667 5.1 8Z" fill="#0C2631" />
              </svg>
              {isFilled.keyText(info.languages) && <p className="body-xs py-1.5 uppercase">{info.languages}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const PropertyListResults = memo(function PropertyListResults({
  loading,
  error,
  displayedCount,
  visibleCardItems,
  emptyInfo,
  isRental,
  showVoirPlus,
  onLoadMore,
}) {
  const contentMinHeight =
    !loading && !error && displayedCount === 0 ? "min-h-0" : "min-h-[500px]";

  return (
    <div className={`flex flex-col items-start justify-start ${contentMinHeight}`}>
      {loading ? (
        <div className="flex flex-col items-center justify-center gap-6 w-full">
          <Vague className="w-full h-full" />
          <p className="body-m text-center text-terracota-400">Chargement…</p>
        </div>
      ) : null}

      {error ? (
        <p className="body-m text-center text-red-600" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && !error ? (
        <div className="flex w-full flex-col items-center justify-center gap-6">
          {displayedCount === 0 ? (
            <EmptyResultsContact info={emptyInfo} />
          ) : (
            <>
              <ul className="grid w-full grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {visibleCardItems.map(({ id, item }) => (
                  <li key={id} className="h-full">
                    <PropertyCard item={item} />
                  </li>
                ))}
              </ul>
              {showVoirPlus ? (
                <button
                  type="button"
                  onClick={onLoadMore}
                  className={`cta cta-text ${isRental ? "cta-bleu-950" : "cta-terracota-400"}`}
                >
                  Voir plus
                </button>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
});

/**
 * @typedef {import("@prismicio/client").Content.PropertyListSlice} PropertyListSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<PropertyListSlice>} PropertyListProps
 * @type {import("react").FC<PropertyListProps>}
 */
const PropertyList = ({ slice, context = {} }) => {
  const { type } = context;
  const isRental = type === "property_rental" || slice.variation === "location";
  const documentType = isRental ? "property_rental" : "property";

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openFilter, setOpenFilter] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobileOpenFilter, setMobileOpenFilter] = useState(null);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const skipFiltersDebounce = useRef(true);
  const fetchGenerationRef = useRef(0);

  const totalSelectedFilterCount = useMemo(
    () => getTotalSelectedFilterCount(draftFilters, FILTERS),
    [draftFilters]
  );
  const hasSelectedFilters = totalSelectedFilterCount > 0;

  const availableCities = useMemo(
    () =>
      getAvailableCities(
        properties.map((doc) => doc.data?.city ?? doc.data?.district ?? null)
      ),
    [properties]
  );

  const displayedProperties = useMemo(
    () =>
      prioritizeCoupDeCoeur(
        filterProperties(
          dedupeDocumentsById(properties),
          appliedFilters,
          documentType
        )
      ),
    [properties, appliedFilters, documentType]
  );

  const visibleProperties = useMemo(
    () => displayedProperties.slice(0, visibleCount),
    [displayedProperties, visibleCount]
  );
  const visibleCardItems = useMemo(
    () =>
      dedupeDocumentsById(visibleProperties).map((doc) => ({
        id: doc.id,
        item: docToCardItem(doc),
      })),
    [visibleProperties]
  );
  const emptyInfo = useMemo(() => {
    const infos = slice.primary?.infos;
    if (Array.isArray(infos)) return infos[0] ?? null;
    return infos ?? null;
  }, [slice.primary?.infos]);

  const hasAtLeastTenResults = displayedProperties.length >= 10;
  const showVoirPlus =
    hasAtLeastTenResults && visibleCount < displayedProperties.length;

  useEffect(() => {
    if (skipFiltersDebounce.current) {
      skipFiltersDebounce.current = false;
      return undefined;
    }

    const timer = setTimeout(() => {
      setAppliedFilters(draftFilters);
      setVisibleCount(PAGE_SIZE);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draftFilters]);

  useEffect(() => {
    if (!mobileFiltersOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileFiltersOpen]);

  useEffect(() => {
    if (!mobileFiltersOpen) {
      setMobileOpenFilter(null);
    }
  }, [mobileFiltersOpen]);

  const updateDraft = useCallback((patch) => {
    setDraftFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  const toggleListItem = useCallback((key, value) => {
    setDraftFilters((prev) => {
      const list = prev[key];
      const next = list.includes(value)
        ? list.filter((item) => item !== value)
        : [...list, value];
      return { ...prev, [key]: next };
    });
  }, []);

  const resetFilters = useCallback(() => {
    setDraftFilters((prev) => {
      if (getTotalSelectedFilterCount(prev, FILTERS) === 0) {
        return prev;
      }
      return EMPTY_FILTERS;
    });
  }, []);

  const handleCloseFilter = useCallback(() => setOpenFilter(null), []);
  const handleToggleFilter = useCallback(
    (id) => setOpenFilter((current) => (current === id ? null : id)),
    []
  );
  const handleToggleMobileFilter = useCallback(
    (id) => setMobileOpenFilter((current) => (current === id ? null : id)),
    []
  );
  const handleLoadMore = useCallback(() => {
    setVisibleCount((count) =>
      Math.min(count + PAGE_SIZE, displayedProperties.length)
    );
  }, [displayedProperties.length]);

  useEffect(() => {
    const fetchGeneration = ++fetchGenerationRef.current;
    const controller = new AbortController();

    async function load() {
      try {
        const response = await fetch(
          `/api/properties?type=${encodeURIComponent(documentType)}`,
          { signal: controller.signal }
        );
        if (!response.ok) throw new Error("Impossible de charger les biens.");
        const data = await response.json();
        if (fetchGeneration !== fetchGenerationRef.current) return;

        setProperties(dedupeDocumentsById(data.documents ?? []));
        setError(null);
        setLoading(false);
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") return;
        if (fetchGeneration !== fetchGenerationRef.current) return;

        setError(err instanceof Error ? err.message : "Une erreur est survenue.");
        setProperties([]);
        setLoading(false);
      }
    }

    setLoading(true);
    setError(null);
    load();

    return () => {
      controller.abort();
    };
  }, [documentType]);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`contre-uno ${isRental ? "bg-terracota-100" : "bg-bleu-50"
        }`}
    >
      <div className="padding-y-rounded framing-lg padding-x flex flex-col gap-8">
        {slice.primary.title ? (
          <h1 className="h4 text-center uppercase text-bleu-950">
            {slice.primary.title}
          </h1>
        ) : (
          <p className="h4 text-center uppercase text-bleu-950">
            Découvrez tous les biens qui correspondent à votre recherche
          </p>
        )}

        <div className="relative hidden flex-wrap items-start justify-center gap-8 pb-10 md:flex">
          {FILTERS.map(({ id, label }) => (
            <FilterButton
              key={id}
              label={label}
              isOpen={openFilter === id}
              indicatorCount={getFilterGroupSelectionCount(draftFilters, id)}
              onToggle={() => handleToggleFilter(id)}
              onClose={handleCloseFilter}
              panelClassName={id === "surface" || id === "budget" ? "min-w-[274px]" : "min-w-[221px]"}
            >
              <FilterPopupContent
                filterId={id}
                draft={draftFilters}
                cities={availableCities}
                onDraftChange={updateDraft}
                onToggleListItem={toggleListItem}
              />
            </FilterButton>
          ))}
        </div>

        <div className="md:hidden">
          <button
            type="button"
            aria-expanded={mobileFiltersOpen}
            onClick={() => setMobileFiltersOpen((current) => !current)}
            className="flex py-2 w-full items-center justify-between gap-3 rounded-lg bg-bleu-900 px-4 text-left text-[18px]/[26px] text-bleu-50 transition-colors hover:bg-bleu-800"
          >
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate">Filtre</span>
              {totalSelectedFilterCount > 0 ? (
                <span className="inline-flex min-w-5 py-0.5 items-center justify-center rounded-full bg-terracota-400 px-1.5 text-[12px]/[18px] font-semibold text-terracota-100">
                  {totalSelectedFilterCount}
                </span>
              ) : null}
            </span>

            <div className={`shrink-0 transition-transform ${mobileFiltersOpen ? "rotate-180" : ""}`}>
              <PlusIcon />
            </div>
          </button>

          {mobileFiltersOpen ? createPortal(
            <div className="fixed inset-0 z-[200] bg-black/10 animate-filter-overlay-in" onClick={() => setMobileFiltersOpen(false)}>
              <div className="flex h-auto bg-bleu-50 items-end flex-col overflow-y-auto px-6 pt-4" onClick={(e) => e.stopPropagation()}>
                <div className="mb-4 flex items-center justify-between gap-4">
                  <button
                    type="button"
                    onClick={() => setMobileFiltersOpen(false)}
                    className=""
                    aria-label="Fermer les filtres"
                  >
                    <svg width="20" height="24" viewBox="0 0 20 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                      <path d="M17.7656 18.6336C17.8399 18.708 17.8988 18.7962 17.9391 18.8933C17.9793 18.9904 18 19.0945 18 19.1996C18 19.3047 17.9793 19.4088 17.9391 19.5059C17.8988 19.603 17.8399 19.6912 17.7656 19.7656C17.6912 19.8399 17.603 19.8988 17.5059 19.9391C17.4088 19.9793 17.3047 20 17.1996 20C17.0945 20 16.9904 19.9793 16.8933 19.9391C16.7962 19.8988 16.708 19.8399 16.6336 19.7656L10 13.1309L3.36637 19.7656C3.21626 19.9157 3.01268 20 2.8004 20C2.58812 20 2.38454 19.9157 2.23443 19.7656C2.08433 19.6155 2 19.4119 2 19.1996C2 18.9873 2.08433 18.7837 2.23443 18.6336L8.86906 12L2.23443 5.36637C2.08433 5.21626 2 5.01268 2 4.8004C2 4.58812 2.08433 4.38454 2.23443 4.23443C2.38454 4.08433 2.58812 4 2.8004 4C3.01268 4 3.21626 4.08433 3.36637 4.23443L10 10.8691L16.6336 4.23443C16.7837 4.08433 16.9873 4 17.1996 4C17.4119 4 17.6155 4.08433 17.7656 4.23443C17.9157 4.38454 18 4.58812 18 4.8004C18 5.01268 17.9157 5.21626 17.7656 5.36637L11.1309 12L17.7656 18.6336Z" fill="#0C2631" />
                    </svg>
                  </button>
                </div>

                <div className="w-full flex flex-col gap-3 pt-4">
                  {FILTERS.map(({ id, label }) => (
                    <MobileFilterSection
                      key={id}
                      label={label}
                      isOpen={mobileOpenFilter === id}
                      indicatorCount={getFilterGroupSelectionCount(draftFilters, id)}
                      onToggle={() => handleToggleMobileFilter(id)}
                    >
                      <FilterPopupContent
                        filterId={id}
                        draft={draftFilters}
                        cities={availableCities}
                        onDraftChange={updateDraft}
                        onToggleListItem={toggleListItem}
                      />
                    </MobileFilterSection>
                  ))}
                </div>

                <div className="sticky bottom-0 flex w-full justify-end bg-bleu-50 pb-6 pt-4">
                  <button
                    type="button"
                    onClick={resetFilters}
                    disabled={!hasSelectedFilters}
                    className={`text-[14px]/[20px] underline underline-offset-2 transition-opacity ${hasSelectedFilters ? "opacity-70 hover:opacity-100" : "opacity-30 cursor-default"}`}
                  >
                    Réinitialiser
                  </button>
                </div>
              </div>
            </div>
            , document.body) : null}
        </div>

        <PropertyListResults
          loading={loading}
          error={error}
          displayedCount={displayedProperties.length}
          visibleCardItems={visibleCardItems}
          emptyInfo={emptyInfo}
          isRental={isRental}
          showVoirPlus={showVoirPlus}
          onLoadMore={handleLoadMore}
        />
      </div>
    </section>
  );
};

export default PropertyList;
